import { useEffect, useState } from "react";
import { InputRadioTypeProps } from "./InputRadioType.type";

const INPUT_COLOR_ERROR = "red";

export default function InputRadioType({
  fieldName,
  label,
  register,
  required,
  error,
  onChange,
  option1,
  option2,
  option1Legend,
  option2Legend,
  defaultValue,
  labelStyles = "",
  inputStyles = "",
  inputsContainerStyles = "",
  globalContainerStyles = "",
  value,
  getValues,
  readOnly,
}: InputRadioTypeProps) {
  const [selected, setSelected] = useState(defaultValue || getValues?.(fieldName));

  const handleInputRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(e.target.value);
    onChange && onChange(e.target.value);
  };

  useEffect(() => {
    setSelected(defaultValue || getValues?.(fieldName));
  }, [defaultValue, fieldName, getValues]);

  return (
    <>
      <div className={`w-full ${globalContainerStyles}`}>
        {label ? <label className={labelStyles}>{label}</label> : null}

        <div className={`mt-2 flex flex-col gap-3 ${inputsContainerStyles}`}>
          <label
            className={`flex cursor-pointer flex-col rounded-md border border-slate-200 bg-white p-3 shadow-sm transition-colors ${
              (value ?? selected) === option1 ? "ring-2 ring-red-300" : ""
            } ${readOnly ? "cursor-not-allowed opacity-70" : ""} ${inputStyles}`}
          >
            <span className="flex items-center justify-between gap-2 font-semibold text-slate-800">
              <span className="flex items-center gap-2">
              <input
                type="radio"
                disabled={readOnly}
                className="text-red-500 focus:ring-red-300"
                {...register(fieldName, { required })}
                value={option1}
                checked={(value ?? selected) === option1 ? true : false}
                onChange={handleInputRadio}
              />
              {option1 ? option1 : undefined}
              </span>

              <span className="group relative inline-flex items-center">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors group-hover:bg-slate-100 group-hover:text-slate-700">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M12 10V16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M12 7H12.01"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute right-0 top-full z-10 mt-2 hidden w-[320px] rounded-md bg-slate-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block"
                >
                  Persona que aún no forma parte de tu empresa, para la cual podrás solicitar únicamente todos los exámenes requeridos para un ingreso.
                </span>
              </span>
            </span>
            {option1Legend ? (
              <span className="mt-1 text-sm !text-red-500">{option1Legend}</span>
            ) : null}
          </label>

          <label
            className={`flex cursor-pointer flex-col rounded-md border border-slate-200 bg-white p-3 shadow-sm transition-colors ${
              (value ?? selected) === option2 ? "ring-2 ring-red-300" : ""
            } ${readOnly ? "cursor-not-allowed opacity-70" : ""} ${inputStyles}`}
          >
            <span className="flex items-center justify-between gap-2 font-semibold text-slate-800">
              <span className="flex items-center gap-2">
              <input
                type="radio"
                disabled={readOnly}
                className="text-red-500 focus:ring-red-300"
                {...register(fieldName, { required })}
                value={option2}
                checked={(value ?? selected) === option2 ? true : false}
                onChange={handleInputRadio}
              />
              {option2 ? option2 : undefined}
              </span>

              <span className="group relative inline-flex items-center">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors group-hover:bg-slate-100 group-hover:text-slate-700">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M12 10V16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M12 7H12.01"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute right-0 top-full z-10 mt-2 hidden w-[320px] rounded-md bg-slate-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block"
                >
                  Persona que ya forma parte de tu nómina y tiene asociado únicamente prácticas tales como ausentismo, exámenes periódicos, examen de egreso, interconsultas, etc.
                </span>
              </span>
            </span>
            {option2Legend ? (
              <span className="mt-1 text-sm !text-red-500">{option2Legend}</span>
            ) : null}
          </label>
        </div>
      </div>

      {error?.message && <span style={{ color: INPUT_COLOR_ERROR }}>{error?.message}</span>}
      {error?.type === "required" && (
        <span style={{ color: INPUT_COLOR_ERROR }}>Campo Obligatorio*</span>
      )}
    </>
  );
}
